import MOMO_APP_IMAGE from "@images/momo.jpg";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function useIntersection(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const FEAT_ICONS = [
  "ri-map-pin-line",
  "ri-group-line",
  "ri-id-card-line",
  "ri-store-2-line",
];
const FEAT_KEYS = ["feat1", "feat2", "feat3", "feat4"] as const;

export default function MOMOSection() {
  const { t } = useTranslation();
  const { ref, visible } = useIntersection();

  return (
    <section
      id="momo"
      className="py-24 md:py-32 px-4"
      style={{ backgroundColor: "oklch(0.13 0.001 10)" }}
    >
      <div className="max-w-7xl mx-auto">
        <div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center"
        >
          {/* Left */}
          <div
            className={`transition-all duration-700 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <p className="text-xs font-label tracking-widest uppercase mb-4" style={{ color: "oklch(0.74 0.18 55)" }}>
              {t("momo.label")}
            </p>
            <h2 className="font-heading font-black text-3xl md:text-5xl leading-tight mb-4" style={{ color: "white" }}>
              {t("momo.title1")}<br />
              {t("momo.title2")} <span style={{ color: "oklch(0.74 0.18 55)" }}>MOMO</span>
            </h2>
            <p
              className="font-label text-sm md:text-base leading-relaxed mb-10 whitespace-pre-line"
              style={{ color: "rgba(255,255,255,0.50)" }}
            >
              {t("momo.desc")}
            </p>

            {/* Features */}
            <div className="space-y-5">
              {FEAT_KEYS.map((key, i) => (
                <div
                  key={key}
                  className={`flex gap-4 transition-all duration-600 ${
                    visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"
                  }`}
                  style={{ transitionDelay: `${i * 100 + 200}ms` }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: "rgba(245,158,11,0.12)" }}
                  >
                    <i className={`${FEAT_ICONS[i]} text-lg`} style={{ color: "oklch(0.74 0.18 55)" }}></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-sm mb-1" style={{ color: "rgba(255,255,255,0.9)" }}>
                      {t(`momo.${key}_title`)}
                    </h4>
                    <p className="text-xs font-label leading-relaxed" style={{ color: "rgba(255,255,255,0.45)" }}>
                      {t(`momo.${key}_desc`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: phone mockup */}
          <div
            className={`flex flex-col items-center gap-6 transition-all duration-700 delay-300 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(245,158,11,0.18) 0%, transparent 70%)",
                  transform: "scale(1.3)",
                }}
              />
              {/* Phone Body */}
              <div
                className="relative mx-auto"
                style={{
                  width: "290px",
                  padding: "12px 6px",
                  borderRadius: "40px",
                  backgroundColor: "oklch(0.20 0.001 10)",
                  boxShadow: "0 0 0 2px oklch(0.30 0.001 10), 0 0 0 4px oklch(0.22 0.001 10)",
                }}
              >
                {/* Side Buttons */}
                <div
                  className="absolute z-20"
                  style={{
                    right: "-4px",
                    top: "120px",
                    width: "3px",
                    height: "30px",
                    borderRadius: "2px",
                    backgroundColor: "oklch(0.30 0.001 10)",
                  }}
                />
                <div
                  className="absolute z-20"
                  style={{
                    right: "-4px",
                    top: "162px",
                    width: "3px",
                    height: "50px",
                    borderRadius: "2px",
                    backgroundColor: "oklch(0.30 0.001 10)",
                  }}
                />
                <div
                  className="absolute z-20"
                  style={{
                    left: "-4px",
                    top: "132px",
                    width: "3px",
                    height: "30px",
                    borderRadius: "2px",
                    backgroundColor: "oklch(0.30 0.001 10)",
                  }}
                />
                <div
                  className="absolute z-20"
                  style={{
                    left: "-4px",
                    top: "172px",
                    width: "3px",
                    height: "30px",
                    borderRadius: "2px",
                    backgroundColor: "oklch(0.30 0.001 10)",
                  }}
                />

                {/* Screen Area */}
                <div
                  className="relative rounded-[28px] overflow-hidden"
                  style={{
                    backgroundColor: "#000",
                    border: "2px solid oklch(0.28 0.001 10)",
                  }}
                >
                  {/* Top Bezel - Notch & Speaker */}
                  <div
                    className="absolute top-0 left-0 right-0 z-20 flex justify-center items-end pb-1.5"
                    style={{ height: "36px", backgroundColor: "#000" }}
                  >
                    {/* Notch */}
                    <div
                      className="rounded-full"
                      style={{
                        width: "70px",
                        height: "22px",
                        backgroundColor: "oklch(0.15 0.001 10)",
                        border: "1px solid oklch(0.25 0.001 10)",
                      }}
                    >
                      {/* Camera dot */}
                      <div
                        className="absolute rounded-full"
                        style={{
                          width: "7px",
                          height: "7px",
                          top: "6px",
                          right: "16px",
                          backgroundColor: "oklch(0.35 0.001 255)",
                          boxShadow: "0 0 2px rgba(100,150,255,0.5)",
                        }}
                      />
                    </div>
                  </div>

                  {/* App Screenshot */}
                  <img
                    src={MOMO_APP_IMAGE}
                    alt="MOMO 앱 스크린샷"
                    className="w-full"
                    style={{ display: "block", paddingTop: "36px", paddingBottom: "28px" }}
                  />

                  {/* Bottom Bezel - Home Indicator */}
                  <div
                    className="absolute bottom-0 left-0 right-0 z-20 flex justify-center items-start pt-1.5"
                    style={{ height: "28px", backgroundColor: "#000" }}
                  >
                    <div
                      className="rounded-full"
                      style={{
                        width: "100px",
                        height: "4px",
                        backgroundColor: "oklch(0.50 0.001 10)",
                        borderRadius: "999px",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* MOMO Install Button */}
            <a
              href="#"
              className="cursor-pointer inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-80"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <span
                className="font-label font-semibold text-sm whitespace-nowrap"
                style={{ color: "oklch(0.74 0.18 55)" }}
              >
                {t("momo.install_cta")}
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}